import { debounce } from 'lodash/fp';
import { createContext, useContext, useEffect, useState } from 'react';
import { createStore, Store } from '@mews/std';
import { BreakpointRangeValue } from './types';

const BreakpointRangeStoreContext = createContext<Store<unknown>>(null as unknown as Store<unknown>);
export function useBreakpointRange<B = BreakpointRangeValue>() {
    const store = useContext(BreakpointRangeStoreContext) as Store<B>;
    const [snapshot, setSnapshot] = useState(store.getSnapshot);
    useEffect(() => store.subscribe(setSnapshot), [store]);
    return snapshot;
}

export interface BreakpointRangeProviderProps<B> {
    getBreakpointRange: () => B;
    resizeDebounce?: number;
    children: React.ReactNode;
}

export function BreakpointRangeProvider<B = BreakpointRangeValue>({
    children,
    getBreakpointRange,
    resizeDebounce = 200,
}: BreakpointRangeProviderProps<B>) {
    const [store] = useState(() => createStore(getBreakpointRange));

    useEffect(() => {
        const controller = new AbortController();
        const updateBreakpointValue = debounce(resizeDebounce, () =>
            store.setSnapshot(getBreakpointRange(), { skipEqualValues: true })
        );
        window.addEventListener('resize', updateBreakpointValue, { signal: controller.signal });

        return () => {
            controller.abort();
            updateBreakpointValue.cancel();
        };
    }, [getBreakpointRange, resizeDebounce, store]);

    return (
        <BreakpointRangeStoreContext.Provider value={store as Store<unknown>}>
            {children}
        </BreakpointRangeStoreContext.Provider>
    );
}
